import React, { useState, useEffect, useRef } from 'react';
import ODGImporter from '../../odg/OdgImporter';

export const Capture = ({ onDuplicate, onNewItem, onEditor, selectedItems = [], stackRootId = null, cardDataMap = {} }) => {
    // State variables
    const [todoInputValue, setTodoInputValue] = useState('');
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [isPlusMenuVisible, setIsPlusMenuVisible] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [importReport, setImportReport] = useState(null);

    // Refs for DOM elements
    const menuBtnRef = useRef(null);
    const plusBtnRef = useRef(null);
    const actionMenuRef = useRef(null);
    const plusMenuRef = useRef(null);
    const todoInputRef = useRef(null);
    const editorRef = useRef(null);
    const odgImporterRef = useRef(null);

    // Function to handle input changes
    const handleInputChange = (e) => {
        setTodoInputValue(e.target.value);
    };

    // Function to handle key press (Enter)
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && todoInputValue.trim()) {
            addNewCard();
        }
    };

    // FIXED: Function to determine the correct parent ID based on selection
    const getCorrectParentId = () => {
        if (selectedItems.length === 0) {
            // No selection: use stack root
            return stackRootId;
        }

        const selectedItemId = selectedItems[0];
        const selectedItemData = cardDataMap[selectedItemId];

        if (!selectedItemData) {
            console.warn(`No data found for selected item ${selectedItemId}, using as parent`);
            return selectedItemId;
        }

        if (selectedItemData.isFolder) {
            // Selected item is a folder: create as child (use selected item's ID as parent)
            console.log(`Creating child of folder ${selectedItemId}`);
            return selectedItemId;
        } else {
            // Selected item is not a folder: create as sibling (use selected item's parent)
            console.log(`Creating sibling of item ${selectedItemId}, using parent ${selectedItemData.ed}`);
            return selectedItemData.ed || stackRootId;
        }
    };

    // FIXED: Modified function to use correct parent logic
    const addNewCard = () => {
        if (todoInputValue.trim()) {
            const parentId = getCorrectParentId();
            
            if (!parentId) {
                alert('No parent available for the new note. Stack root ID is missing.');
                return;
            }

            console.log(`Creating new item with parent: ${parentId}`);

            // This will be handled by the parent component which has access to dupQwanta
            if (onNewItem) {
                // Pass the source ID and the title from the input
                onNewItem('6825851dbc2a9dd6f292da75', todoInputValue, parentId)();
            }

            // Clear the input field
            setTodoInputValue('');
        }
    };

    // Function to toggle the action menu
    const toggleMenu = () => {
        setIsMenuVisible(!isMenuVisible);
        if (isPlusMenuVisible) setIsPlusMenuVisible(false);
    };

    // Function to toggle the plus menu
    const togglePlusMenu = () => {
        setIsPlusMenuVisible(!isPlusMenuVisible);
        if (isMenuVisible) setIsMenuVisible(false);
    };

    // Function to toggle the text editor
    const toggleEditor = () => {
        setIsEditorOpen(!isEditorOpen);

        // Focus on editor when opened
        if (!isEditorOpen && editorRef.current) {
            setTimeout(() => {
                editorRef.current.focus();
            }, 0);
        }
    };

    // FIXED: Function to save text from editor - use correct parent logic
    const saveEditorContent = () => {
        if (editorRef.current && editorRef.current.value.trim()) {
            const lines = editorRef.current.value.split('\n');
            const title = lines[0] || 'New note';
            const content = lines.slice(1).join('\n').trim();

            const parentId = getCorrectParentId();
            
            if (!parentId) {
                alert('No parent available for the new note. Stack root ID is missing.');
                return;
            }

            console.log(`Creating new editor item with parent: ${parentId}`);

            if (onNewItem) {
                // Create new item with title and content
                onNewItem('6825851dbc2a9dd6f292da75', title, parentId)();
            }

            // Clear and close editor
            editorRef.current.value = '';
            setIsEditorOpen(false);
        }
    };

    // FIXED: Modified function to handle action selection with correct parent logic
    const handleAction = (action) => {
        const parentId = getCorrectParentId();
        
        if (!parentId) {
            alert('No parent available for new items. Stack root ID is missing.');
            return;
        }

        if (action === 'copy' && onDuplicate) {
            if (selectedItems.length === 0) {
                alert('Please select an item to copy');
            } else {
                onDuplicate();
            }
        } else if (action === 'jsx' && onNewItem) {
            console.log(`Creating JSX item with parent: ${parentId}`);
            // Pass only the source ID without a title for JSX items
            onNewItem('6810522c7a4481c9ccee2fab', null, parentId)();
        } else if (action === 'editor' && onNewItem) {
            console.log(`Creating editor item with parent: ${parentId}`);
            onNewItem('682f783954edb74b5f775c96', null, parentId)();
        } else {
            alert(`Action selected: ${action}`);
        }
        setIsMenuVisible(false);
    };

    // FIXED: Function to handle plus menu actions with correct parent logic
    const handlePlusAction = (action) => {
        if (action === 'odg') {
            // Trigger the ODG file input
            if (odgImporterRef.current) {
                const fileInput = odgImporterRef.current.querySelector('input[type="file"]');
                if (fileInput) {
                    fileInput.click();
                }
            }
        } else if (action === 'text') {
            toggleEditor();
        } else {
            alert(`Plus action selected: ${action}`);
        }
        setIsPlusMenuVisible(false);
    };

    // Handle import complete
    const handleImportComplete = (reportText) => {
        setImportReport(reportText);
        alert('ODG Import completed. Check console for details.');
        console.log(reportText);
    };

    // Set up click outside handler to close the menus
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                isMenuVisible &&
                menuBtnRef.current &&
                actionMenuRef.current &&
                !menuBtnRef.current.contains(e.target) &&
                !actionMenuRef.current.contains(e.target)
            ) {
                setIsMenuVisible(false);
            }

            if (
                isPlusMenuVisible &&
                plusBtnRef.current &&
                plusMenuRef.current &&
                !plusBtnRef.current.contains(e.target) &&
                !plusMenuRef.current.contains(e.target)
            ) {
                setIsPlusMenuVisible(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isMenuVisible, isPlusMenuVisible]);

    return (
        <div className="inbox-container" style={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Input Card */}
            <div className="field m-0">
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div style={{ flexGrow: 1, marginRight: '10px' }}>
                        <input
                            id="todo-input"
                            ref={todoInputRef}
                            className="input is-small"
                            type="text"
                            placeholder="Add a todo, or use / for actions"
                            style={{ width: '100%' }}
                            value={todoInputValue}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                        />
                    </div>
                    <span className="icon is-small" style={{ cursor: 'pointer' }} onClick={toggleEditor}>
                        <i className="fas fa-edit"></i>
                    </span>
                </div>
            </div>

            <div className="level is-mobile m-0 mt-2">
                <div className="level-left">
                    <div className="level-item">
                        <button
                            ref={plusBtnRef}
                            className="button is-white is-small p-1"
                            onClick={togglePlusMenu}
                        >
                            <span className="icon is-small">
                                <i className="fas fa-plus"></i>
                            </span>
                        </button>
                    </div>
                    <div className="level-item">
                        <button
                            className="button is-white is-small p-1"
                            onClick={() => alert('Camera functionality would be implemented here')}
                        >
                            <span className="icon is-small">
                                <i className="fas fa-camera"></i>
                            </span>
                        </button>
                    </div>
                    <div className="level-item">
                        <button
                            className="button is-white is-small p-1"
                            onClick={() => alert('Voice recording functionality would be implemented here')}
                        >
                            <span className="icon is-small">
                                <i className="fas fa-microphone"></i>
                            </span>
                        </button>
                    </div>
                    <div className="level-item">
                        <button
                            ref={menuBtnRef}
                            className="button is-white is-small p-1"
                            onClick={toggleMenu}
                        >
                            <span className="icon is-small">
                                <i className="fas fa-ellipsis-h"></i>
                            </span>
                        </button>
                    </div>
                </div>
                <div className="level-right">
                    <div className="level-item">
                        <button
                            className="button is-white is-small p-1"
                            onClick={addNewCard}
                        >
                            <span className="icon is-small">
                                <i className="fas fa-paper-plane"></i>
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Plus menu - positioned relative to the plus button */}
            <div
                ref={plusMenuRef}
                className={`plus-menu ${isPlusMenuVisible ? '' : 'is-hidden'}`}
                style={{
                    position: 'absolute',
                    zIndex: 10,
                    marginTop: '5px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '10px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
            >
                <div>
                    <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handlePlusAction('odg'); }}>
                            <span className="icon is-small mr-2">
                                <i className="fas fa-file-import"></i>
                            </span>
                            Import ODG File
                        </a>
                    </p>
                    <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handlePlusAction('text'); }}>
                            <span className="icon is-small mr-2">
                                <i className="fas fa-file-alt"></i>
                            </span>
                            New Text Note
                        </a>
                    </p>
                </div>
            </div>

            {/* Action menu - positioned relative to the input */}
            <div
                ref={actionMenuRef}
                className={`action-menu ${isMenuVisible ? '' : 'is-hidden'}`}
                style={{
                    position: 'absolute',
                    zIndex: 10,
                    marginTop: '5px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '10px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
            >
                <div>
                    <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleAction('copy'); }}>
                            <span className="icon is-small mr-2">
                                <i className="fas fa-copy"></i>
                            </span>
                            Copy card
                        </a>
                    </p>
                    <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleAction('jsx'); }}>
                            <span className="icon is-small mr-2">
                                <i className="mdi mdi-code-json"></i>
                            </span>
                            Minimal JSX
                        </a>
                    </p>
                    <p style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleAction('editor'); }}>
                            <span className="icon is-small mr-2">
                                <i className="fas fa-edit"></i>
                            </span>
                            Editor
                        </a>
                    </p>
                </div>
            </div>

            {/* Text Editor (hidden by default) */}
            <div
                className={`editor-container ${isEditorOpen ? '' : 'is-hidden'}`}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 20,
                    padding: '1rem'
                }}
            >
                <div style={{ width: '100%' }}>
                    <textarea
                        ref={editorRef}
                        style={{
                            width: '100%',
                            minHeight: '120px',
                            marginBottom: '10px',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                        placeholder="Enter title on first line&#10;Add details below..."
                        rows="6"
                    ></textarea>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button
                            className="button is-small"
                            onClick={toggleEditor}
                        >
                            Cancel
                        </button>
                        <button
                            className="button is-small is-primary"
                            onClick={saveEditorContent}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
            
            {/* ODG Importer component (hidden) */}
            <div ref={odgImporterRef} style={{ display: 'none' }}>
                <ODGImporter onImportComplete={handleImportComplete} />
            </div>
        </div>
    );
};

export default Capture;